/**
 * Pure Net-LO-Locked + pricing-excess math.
 *
 * Extracted from `usePricingRecalc.ts` (the Product & Pricing workspace) so the
 * SAME above-par-excess math is the single source of truth for live pricing
 * displays and the commission report's residual calculation inputs.
 *
 * Net LO Locked waterfall (1 point = 1% of loan):
 *   totalLoLocked    = basePriceLocked + discountPoints + concessionPoints
 *   adjustedLoLocked = totalLoLocked + locrPoints            (LO comp reduction)
 *   netLoLocked      = adjustedLoLocked − lenderCreditPoints − curePoints
 *   pricingExcess = (round3(netLoLocked) − 100) × loanAmount / 100
 *
 * No React, no hooks — keep it that way.
 */

export interface NetLoLockedInput {
  /** `rate_lock_total_buy_price` — price locked after LLPAs. */
  basePriceLocked: number;
  discountPoints: number;
  /** Σ active (non-legacy) concession points. */
  concessionPoints: number;
  /** Applied LO comp reduction in points (= applied reduction bps / 100). */
  locrPoints: number;
  /** Total issued lender credit, in points. */
  lenderCreditPoints: number;
  /** Cure applied, in points. */
  curePoints: number;
  loanAmount: number;
}

export interface NetLoLockedResult {
  totalLoLocked: number;
  adjustedLoLocked: number;
  netLoLocked: number;
  /** netLoLocked rounded to 3 decimals — the value shown and used for pricing excess. */
  displayedNetLoLocked: number;
  /** Above-par pricing excess of the displayed price in dollars. */
  loProfitDollars: number;
}

/** Match the pricing UI's display rounding (3 decimals). */
export const roundPriceForDisplay = (value: number): number => Number(value.toFixed(3));

/** 1 point = 1% of loan. */
export const pointsToDollarsAt = (loanAmount: number, points: number): number =>
  points * loanAmount * 0.01;

/** Dollars → points at the given loan amount. */
export const dollarsToPointsAt = (loanAmount: number, dollars: number): number =>
  loanAmount <= 0 ? 0 : dollars / (loanAmount * 0.01);

/** Convert a Net LO Locked price to above-par dollars at the given loan amount. */
export function computeLoProfitability(netLoLocked: number, loanAmount: number): number {
  return (netLoLocked - 100) * (loanAmount / 100);
}

/** Full Net-LO-Locked waterfall from already-derived point components. */
export function computeNetLoLocked(input: NetLoLockedInput): NetLoLockedResult {
  const totalLoLocked = input.basePriceLocked + input.discountPoints + input.concessionPoints;
  const adjustedLoLocked = totalLoLocked + input.locrPoints;
  const netLoLocked = adjustedLoLocked - input.lenderCreditPoints - input.curePoints;
  const displayedNetLoLocked = roundPriceForDisplay(netLoLocked);
  return {
    totalLoLocked,
    adjustedLoLocked,
    netLoLocked,
    displayedNetLoLocked,
    loProfitDollars: computeLoProfitability(displayedNetLoLocked, input.loanAmount)
  };
}
